/**
 * FlashBook — Reading-Audio Editor
 *
 * A creator records their reading in the studio; this is the mastering desk
 * that sits behind it. It exists because a phone microphone indoors sounds thin
 * and roomy, and a book that narrates itself badly is worse than one that does
 * not narrate at all.
 *
 * The design leans on the two things the platform already gives us, so no
 * dependency is added for any of it:
 *
 *  • `OfflineAudioContext` renders the tone/space effects faster than
 *    real-time, so a preview is ready before the click finishes.
 *  • The sample pass (gate, fades, make-up gain, soft limiter) runs in plain
 *    JavaScript over the rendered channels — a true gate needs per-sample
 *    envelope following, which is not expressible as a stock node.
 *
 * Everything after the render is a pure function over `AudioBufferLike`, which
 * is why the editor is testable in a plain node environment.
 */
import {getAudioEngine} from '#/lib/flashbookAudioEngine'
import {clipSourceFor} from '#/lib/flashbookClips'

/** The slice of `AudioBuffer` this module needs, so tests can pass a stub. */
export type AudioBufferLike = {
  length: number
  sampleRate: number
  numberOfChannels: number
  duration: number
  getChannelData: (channel: number) => Float32Array
}

/** A range of the source clip, as fractions of its length. */
export type AudioTrim = {
  start: number
  end: number
}

/**
 * The editor's controls.
 *
 * Deliberately named after what a creator hears rather than after the DSP —
 * "Clarity", not "+8 dB peaking @ 2.6 kHz". Every `0..1` dial is a fraction of
 * its own range, so defaults are all-zero and "flat" is a real, checkable state.
 */
export type AudioEditSettings = {
  /** Presence — consonants and diction cut through. */
  clarity: number
  /** Body — chest tone, the opposite of a phone-mic recording. */
  warmth: number
  /** Authority — narrator weight: low end up, brittle highs down. */
  depth: number
  /** Air — a lifted top end for a bright, close feel. */
  brightness: number
  /** Space — algorithmic reverb wet. */
  room: number
  /** Slap-back delay wet, for a hall or a broadcast sting. */
  echo: number
  /** Hum, rumble and room-tone removal (high-pass + envelope gate). */
  cleanup: number
  /** Evenness — compression, so quiet words stay audible next to loud ones. */
  evenness: number
  /** Make-up gain, 1 = unchanged. */
  volume: number
  /** Playback rate, 1 = unchanged. */
  speed: number
  /** Seconds of fade in / out at the clip's edges. */
  fadeIn: number
  fadeOut: number
}

/** A flat clip: no colour, no space, unity gain. */
export const FLAT_SETTINGS: AudioEditSettings = {
  clarity: 0,
  warmth: 0,
  depth: 0,
  brightness: 0,
  room: 0,
  echo: 0,
  cleanup: 0,
  evenness: 0,
  volume: 1,
  speed: 1,
  fadeIn: 0,
  fadeOut: 0,
}

export type AudioPresetId =
  | 'natural'
  | 'studio'
  | 'warm'
  | 'narrator'
  | 'bright'
  | 'radio'
  | 'hall'
  | 'cathedral'

export type AudioPreset = {
  id: AudioPresetId
  /** Shown on the chip — kept as a key so the UI can translate it. */
  label: string
  /** One line of intent, for the accessibility hint. */
  intent: string
  settings: AudioEditSettings
}

/**
 * The presets are the editor's front door: most creators will pick one and
 * stop, so each has to be recognisably different rather than a colour swap.
 */
export const AUDIO_PRESETS: AudioPreset[] = [
  {
    id: 'natural',
    label: 'Natural',
    intent: 'Cleans up the room without changing your voice',
    settings: {...FLAT_SETTINGS, cleanup: 0.35, evenness: 0.2, volume: 1.06},
  },
  {
    id: 'studio',
    label: 'Studio',
    intent: 'Close-mic podcast sound: clear, even and warm',
    settings: {
      ...FLAT_SETTINGS,
      clarity: 0.55,
      warmth: 0.35,
      evenness: 0.55,
      room: 0.12,
      cleanup: 0.4,
      volume: 1.12,
    },
  },
  {
    id: 'warm',
    label: 'Warm',
    intent: 'Soft and friendly, for a bedtime or storybook read',
    settings: {
      ...FLAT_SETTINGS,
      warmth: 0.75,
      depth: 0.3,
      brightness: 0,
      cleanup: 0.35,
      evenness: 0.25,
      volume: 1.08,
    },
  },
  {
    id: 'narrator',
    label: 'Narrator',
    intent: 'Authoritative and steady, for reference material',
    settings: {
      ...FLAT_SETTINGS,
      depth: 0.75,
      brightness: 0.25,
      evenness: 0.6,
      room: 0.18,
      cleanup: 0.45,
      volume: 1.12,
    },
  },
  {
    id: 'bright',
    label: 'Bright',
    intent: 'Lifted and energetic, for young learners',
    settings: {
      ...FLAT_SETTINGS,
      clarity: 0.7,
      brightness: 0.6,
      evenness: 0.4,
      cleanup: 0.4,
      volume: 1.1,
    },
  },
  {
    id: 'radio',
    label: 'Radio',
    intent: 'Punchy and tight, like a broadcast spot',
    settings: {
      ...FLAT_SETTINGS,
      clarity: 0.85,
      brightness: 0.35,
      depth: 0.25,
      evenness: 0.8,
      cleanup: 0.55,
      volume: 1.16,
    },
  },
  {
    id: 'hall',
    label: 'Hall',
    intent: 'A lecture theatre: a real sense of place',
    settings: {
      ...FLAT_SETTINGS,
      clarity: 0.35,
      evenness: 0.4,
      room: 0.6,
      echo: 0.2,
      cleanup: 0.4,
      volume: 1.1,
    },
  },
  {
    id: 'cathedral',
    label: 'Cathedral',
    intent: 'Long stone tail, for ceremonial or heritage readings',
    settings: {
      ...FLAT_SETTINGS,
      warmth: 0.3,
      evenness: 0.35,
      room: 0.95,
      echo: 0.45,
      cleanup: 0.4,
      volume: 1.14,
    },
  },
]

export function getAudioPreset(id: AudioPresetId): AudioPreset {
  return AUDIO_PRESETS.find(p => p.id === id) ?? AUDIO_PRESETS[0]
}

/** The preset a set of settings matches exactly, if any (drives the chip row). */
export function matchAudioPreset(
  settings: AudioEditSettings,
): AudioPresetId | null {
  const found = AUDIO_PRESETS.find(p => sameSettings(p.settings, settings))
  return found ? found.id : null
}

export function sameSettings(
  a: AudioEditSettings,
  b: AudioEditSettings,
): boolean {
  return (Object.keys(FLAT_SETTINGS) as (keyof AudioEditSettings)[]).every(
    key => Math.abs(a[key] - b[key]) < 1e-6,
  )
}

export function isFlat(settings: AudioEditSettings): boolean {
  return sameSettings(settings, FLAT_SETTINGS)
}

/**
 * A stable key for a settings+trim pair, so the rendered preview can be cached
 * and a slider that has not actually moved does not re-render the clip.
 */
export function editKey(
  settings: AudioEditSettings,
  trim: AudioTrim = {start: 0, end: 1},
): string {
  const parts = (Object.keys(FLAT_SETTINGS) as (keyof AudioEditSettings)[]).map(
    key => settings[key].toFixed(4),
  )
  return [...parts, trim.start.toFixed(5), trim.end.toFixed(5)].join('|')
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(max, Math.max(min, value))
}

/** Decibels to a linear multiplier. */
export function dbToGain(db: number): number {
  return Math.pow(10, db / 20)
}

/** A linear multiplier as decibels (`-Infinity` for silence). */
export function gainToDb(gain: number): number {
  if (gain <= 0) return -Infinity
  return 20 * Math.log10(gain)
}

// ─── Analysis ─────────────────────────────────────────────────────────────────

/**
 * Peak envelope for the waveform display.
 *
 * One value per bin, 0–1, taken as the loudest sample across channels — a peak
 * view rather than an RMS one, because a creator trims against the spikes.
 */
export function computePeaks(buffer: AudioBufferLike, bins = 160): number[] {
  const count = Math.max(1, Math.floor(bins))
  const out = new Array<number>(count).fill(0)
  if (buffer.length <= 0) return out
  const step = buffer.length / count
  const channels = Math.max(1, buffer.numberOfChannels)
  const data: Float32Array[] = []
  for (let c = 0; c < channels; c++) data.push(buffer.getChannelData(c))

  for (let i = 0; i < count; i++) {
    const from = Math.floor(i * step)
    const to = Math.min(
      buffer.length,
      Math.max(from + 1, Math.floor((i + 1) * step)),
    )
    let peak = 0
    for (const channel of data) {
      for (let s = from; s < to; s++) {
        const a = Math.abs(channel[s])
        if (a > peak) peak = a
      }
    }
    out[i] = clamp(peak, 0, 1)
  }
  return out
}

/** The loudest sample in the clip, across channels. */
export function peakLevel(buffer: AudioBufferLike): number {
  let peak = 0
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c)
    for (let i = 0; i < data.length; i++) {
      const a = Math.abs(data[i])
      if (a > peak) peak = a
    }
  }
  return clamp(peak, 0, 1)
}

/**
 * The gain that brings the clip's loudest moment to `targetDb`.
 *
 * Normalising is the one-click fix for the most common complaint about a
 * recording — "it's too quiet next to the other pages".
 */
export function normalizeGain(buffer: AudioBufferLike, targetDb = -1): number {
  const peak = peakLevel(buffer)
  if (peak <= 1e-6) return 1
  return clamp(dbToGain(targetDb) / peak, 0.1, 24)
}

/**
 * Where the voice actually starts and ends, so the dead air around it can go.
 *
 * Returns fractions of the clip. `threshold` is linear; the padding keeps a
 * little breath on each side so the trim never clips the first consonant.
 */
export function detectSilenceTrim(
  buffer: AudioBufferLike,
  opts?: {thresholdDb?: number; paddingMs?: number; bins?: number},
): AudioTrim {
  const threshold = dbToGain(opts?.thresholdDb ?? -42)
  const padding = (opts?.paddingMs ?? 120) / 1000
  const bins = Math.max(1, opts?.bins ?? 400)
  const peaks = computePeaks(buffer, bins)
  let first = -1
  let last = -1
  for (let i = 0; i < peaks.length; i++) {
    if (peaks[i] >= threshold) {
      if (first < 0) first = i
      last = i
    }
  }
  // All quiet (or all loud): there is nothing to trim, so do not guess.
  if (first < 0 || last < first) return {start: 0, end: 1}
  const duration = buffer.duration > 0 ? buffer.duration : 1
  const pad = padding / duration
  const start = clamp(first / peaks.length - pad, 0, 0.98)
  const end = clamp((last + 1) / peaks.length + pad, start + 0.02, 1)
  return {start, end}
}

/** True when a trim would actually change anything worth re-rendering for. */
export function isMeaningfulTrim(trim: AudioTrim): boolean {
  return trim.start > 0.002 || trim.end < 0.998
}

// ─── Sample pass ──────────────────────────────────────────────────────────────

/**
 * An envelope-followed noise gate, in place.
 *
 * A stock `DynamicsCompressor` cannot do this: gating is a *downward* expander
 * working on a smoothed envelope, and it has to reject the steady low-level
 * hiss between phrases without clipping the tail of a soft word. The follower
 * has a fast attack and a slow release so words open instantly and close
 * gently.
 */
export function applyNoiseGate(
  data: Float32Array,
  threshold: number,
  sampleRate: number,
): void {
  if (threshold <= 0 || data.length === 0) return
  const attack = Math.exp(-1 / (0.004 * sampleRate))
  const release = Math.exp(-1 / (0.11 * sampleRate))
  let env = 0
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i])
    env =
      a > env
        ? attack * env + (1 - attack) * a
        : release * env + (1 - release) * a
    if (env < threshold) {
      // Soft knee: fade proportionally rather than switching to zero, which
      // would sound like a broken connection.
      data[i] *= clamp(env / threshold, 0, 1)
    }
  }
}

/** Linear fade in/out at the clip's edges, in place. */
export function applyFades(
  data: Float32Array,
  sampleRate: number,
  fadeInSeconds: number,
  fadeOutSeconds: number,
): void {
  const len = data.length
  if (len <= 0) return
  const inSamples = Math.min(
    len,
    Math.max(0, Math.round(fadeInSeconds * sampleRate)),
  )
  for (let i = 0; i < inSamples; i++) data[i] *= i / inSamples
  const outSamples = Math.min(
    len - inSamples,
    Math.max(0, Math.round(fadeOutSeconds * sampleRate)),
  )
  for (let i = 0; i < outSamples; i++) {
    data[len - 1 - i] *= i / outSamples
  }
}

/**
 * Evenness — compression, as a sample pass.
 *
 * The one effect in the desk that is not a Web Audio node, and that is a
 * decision rather than a gap: `DynamicsCompressorNode` is the single node
 * `react-native-audio-api` does not implement, so a node-based compressor would
 * make one dial mean two different things on a phone and in a browser. Here it
 * is one implementation, one curve and identical output on every platform — and
 * a test that needs no audio device at all.
 *
 * The semantics are the node's, so the presets keep meaning what they meant:
 * peak detection, a soft knee, 6 ms attack and 220 ms release, and gain
 * *reduction only*. There is no make-up gain because evening out a reading is the
 * job and raising the whole result is what `Volume` and `Normalise` are for.
 */
export type CompressorSettings = {
  thresholdDb: number
  /** Width of the soft knee, in dB. */
  kneeDb: number
  ratio: number
  attackSeconds: number
  releaseSeconds: number
}

export function applyCompression(
  data: Float32Array,
  settings: CompressorSettings,
  sampleRate: number,
): void {
  if (data.length === 0 || settings.ratio <= 1) return
  const attack =
    1 - Math.exp(-1 / (Math.max(0.0005, settings.attackSeconds) * sampleRate))
  const release =
    1 - Math.exp(-1 / (Math.max(0.01, settings.releaseSeconds) * sampleRate))
  const knee = Math.max(0, settings.kneeDb)
  const halfKnee = knee / 2
  const slope = 1 - 1 / Math.max(1, settings.ratio)
  /* The reduction being applied, in dB. Never positive: a compressor only ever
     takes gain away. */
  let reductionDb = 0

  for (let i = 0; i < data.length; i++) {
    const level = Math.abs(data[i])
    const overDb =
      level > 0 ? 20 * Math.log10(level) - settings.thresholdDb : -Infinity
    let targetDb = 0
    if (overDb >= halfKnee) {
      targetDb = -slope * overDb
    } else if (overDb > -halfKnee) {
      /*
       * The soft knee, from the standard gain computer: the reduction eases in
       * across the knee width rather than switching on at the threshold, which is
       * what keeps a quiet word from being treated as a loud one at the boundary.
       */
      const into = overDb + halfKnee
      targetDb = (-slope * into * into) / (2 * Math.max(1e-6, knee))
    }
    // Fast to clamp, slow to let go — the shape the node used.
    const coefficient = targetDb < reductionDb ? attack : release
    reductionDb += (targetDb - reductionDb) * coefficient
    if (reductionDb < -0.01) data[i] *= dbToGain(reductionDb)
  }
}

/**
 * Make-up gain with a soft ceiling.
 *
 * A plain multiply would clip the peaks once "Volume" is pushed for a quiet
 * recording, so anything above the knee is eased into the ceiling instead of
 * squared off — the difference between "louder" and "distorted".
 */
export function applyGainAndLimit(
  data: Float32Array,
  gain: number,
  ceiling = 0.98,
  knee = 0.7,
): void {
  if (data.length === 0) return
  for (let i = 0; i < data.length; i++) {
    let v = data[i] * gain
    const a = Math.abs(v)
    if (a > knee) {
      const over = a - knee
      const headroom = Math.max(1e-6, ceiling - knee)
      const limited = knee + headroom * (1 - Math.exp(-over / headroom))
      v = v < 0 ? -limited : limited
    }
    data[i] = clamp(v, -1, 1)
  }
}

// ─── Effect graph ─────────────────────────────────────────────────────────────

/** A cheap algorithmic reverb tail: decaying noise, longer and darker as it grows. */
export function makeImpulse(
  ctx: BaseAudioContext,
  seconds: number,
  decay: number,
): AudioBuffer {
  const rate = Math.max(8000, Math.floor(ctx.sampleRate))
  const len = Math.max(1, Math.floor(rate * Math.max(0.05, seconds)))
  const impulse = ctx.createBuffer(1, len, rate)
  const data = impulse.getChannelData(0)
  for (let i = 0; i < len; i++) {
    data[i] =
      (Math.random() * 2 - 1) * Math.pow(1 - i / len, Math.max(0.5, decay))
  }
  return impulse
}

export type EffectChain = {
  input: AudioNode
  output: AudioNode
}

/**
 * The tone-and-space half of the edit, as a Web Audio graph.
 *
 * Shared by the offline render and by any live audition, so what a creator
 * hears while dialling is exactly what gets rendered. Good practice and the
 * reason this is exported rather than inlined.
 */
export function buildEffectChain(
  ctx: BaseAudioContext,
  settings: AudioEditSettings,
): EffectChain {
  const input = ctx.createGain()
  let tail: AudioNode = input
  const append = (node: AudioNode) => {
    tail.connect(node)
    tail = node
  }

  // Rumble and hum go first, so everything after them is dealing with voice.
  const highpass = ctx.createBiquadFilter()
  highpass.type = 'highpass'
  highpass.frequency.value = 55 + settings.cleanup * 75
  highpass.Q.value = 0.7
  append(highpass)

  if (settings.warmth > 0.001) {
    const shelf = ctx.createBiquadFilter()
    shelf.type = 'lowshelf'
    shelf.frequency.value = 240
    shelf.gain.value = settings.warmth * 7
    append(shelf)
  }

  if (settings.depth > 0.001) {
    const body = ctx.createBiquadFilter()
    body.type = 'lowshelf'
    body.frequency.value = 120
    body.gain.value = settings.depth * 6
    append(body)
    // Authority is as much about the brittle highs leaving as the lows arriving.
    const tame = ctx.createBiquadFilter()
    tame.type = 'highshelf'
    tame.frequency.value = 6200
    tame.gain.value = -settings.depth * 5
    append(tame)
  }

  if (settings.clarity > 0.001) {
    const presence = ctx.createBiquadFilter()
    presence.type = 'peaking'
    presence.frequency.value = 2600
    presence.Q.value = 1.1
    presence.gain.value = settings.clarity * 8
    append(presence)
  }

  if (settings.brightness > 0.001) {
    const air = ctx.createBiquadFilter()
    air.type = 'highshelf'
    air.frequency.value = 7500
    air.gain.value = settings.brightness * 7
    append(air)
  }

  // `Evenness` is deliberately *not* here — see `applyCompression`.

  const output = ctx.createGain()
  const space = clamp(Math.max(settings.room, settings.echo), 0, 1)
  // Keep the dry signal dominant: wet-only speech is unintelligible.
  const dry = ctx.createGain()
  dry.gain.value = 1 - space * 0.35
  tail.connect(dry)
  dry.connect(output)

  if (settings.room > 0.001) {
    const convolver = ctx.createConvolver()
    convolver.buffer = makeImpulse(ctx, 0.35 + settings.room * 2.6, 2.4)
    const wet = ctx.createGain()
    wet.gain.value = settings.room * 0.5
    tail.connect(convolver)
    convolver.connect(wet)
    wet.connect(output)
  }

  if (settings.echo > 0.001) {
    const delay = ctx.createDelay(1)
    delay.delayTime.value = 0.18 + settings.echo * 0.14
    const feedback = ctx.createGain()
    feedback.gain.value = 0.12 + settings.echo * 0.34
    const wet = ctx.createGain()
    wet.gain.value = settings.echo * 0.38
    tail.connect(delay)
    delay.connect(feedback)
    feedback.connect(delay)
    delay.connect(wet)
    wet.connect(output)
  }

  return {input, output}
}

// ─── Render ───────────────────────────────────────────────────────────────────

/** Copies `[start, end)` of a clip into a fresh context-owned buffer. */
function sliceBuffer(
  ctx: BaseAudioContext,
  source: AudioBufferLike,
  startSample: number,
  endSample: number,
): AudioBuffer {
  const channels = clamp(Math.round(source.numberOfChannels), 1, 2)
  const length = Math.max(1, endSample - startSample)
  const out = ctx.createBuffer(channels, length, source.sampleRate)
  for (let c = 0; c < channels; c++) {
    const src = source.getChannelData(Math.min(c, source.numberOfChannels - 1))
    out.getChannelData(c).set(src.subarray(startSample, startSample + length))
  }
  return out
}

/**
 * Renders the edited clip.
 *
 * Order matters and is deliberate: trim → gate → evenness → tone/space → fades,
 * make-up gain and limiting. Gating *before* the reverb is what keeps a gate from
 * chopping the tail it just produced; compressing there too is what stops the
 * room from pumping with the words, since the reverb is fed a signal whose levels
 * have already been evened out; limiting *last* is what stops the make-up gain
 * from clipping.
 */
export async function renderEditedAudio(
  source: AudioBufferLike,
  settings: AudioEditSettings,
  trim: AudioTrim = {start: 0, end: 1},
): Promise<AudioBuffer> {
  if (!getAudioEngine().available()) {
    throw new Error('this build has no audio engine to render with')
  }
  const speed = clamp(settings.speed, 0.5, 2)
  const startFraction = clamp(trim.start, 0, 0.99)
  const endFraction = clamp(trim.end, startFraction + 0.01, 1)
  const startSample = Math.floor(source.length * startFraction)
  const endSample = Math.max(
    startSample + 1,
    Math.floor(source.length * endFraction),
  )
  const sourceLength = endSample - startSample
  const outLength = Math.max(1, Math.ceil(sourceLength / speed))
  const channels = clamp(Math.round(source.numberOfChannels), 1, 2)

  const ctx = getAudioEngine().createOfflineContext(
    channels,
    outLength,
    source.sampleRate,
  )
  const sliced = sliceBuffer(ctx, source, startSample, endSample)

  if (settings.cleanup > 0.001) {
    const threshold = settings.cleanup * 0.06
    for (let c = 0; c < sliced.numberOfChannels; c++) {
      applyNoiseGate(sliced.getChannelData(c), threshold, sliced.sampleRate)
    }
  }

  if (settings.evenness > 0.001) {
    const compressor: CompressorSettings = {
      thresholdDb: -6 - settings.evenness * 20,
      kneeDb: 12 + settings.evenness * 18,
      ratio: 2 + settings.evenness * 9,
      attackSeconds: 0.006,
      releaseSeconds: 0.22,
    }
    for (let c = 0; c < sliced.numberOfChannels; c++) {
      applyCompression(sliced.getChannelData(c), compressor, sliced.sampleRate)
    }
  }

  const node = ctx.createBufferSource()
  node.buffer = sliced
  node.playbackRate.value = speed
  const chain = buildEffectChain(ctx, settings)
  node.connect(chain.input)
  chain.output.connect(ctx.destination)
  node.start(0)

  const rendered = await ctx.startRendering()

  // The post pass: fade the edges, then lift the whole thing and catch the
  // peaks that the lift would otherwise clip.
  for (let c = 0; c < rendered.numberOfChannels; c++) {
    const data = rendered.getChannelData(c)
    applyFades(data, rendered.sampleRate, settings.fadeIn, settings.fadeOut)
    applyGainAndLimit(data, clamp(settings.volume, 0, 4))
  }

  return rendered
}

// ─── Level matching ───────────────────────────────────────────────────────────

/**
 * Rewrites a clip at a new level, and does nothing else to it.
 *
 * Deliberately *not* `renderEditedAudio` with a flat preset and the gain in
 * `volume`. That path is a mastering chain, and it would quietly re-shape every
 * page it was asked to level: the shelving filter runs even at zero, and
 * `applyGainAndLimit` — the desk's soft ceiling — softens every sample above its
 * 0.7 knee, which on a take that already peaks near full scale is a change the
 * creator neither asked for nor can hear the reason for. A level match has one
 * job, so it is a multiply.
 *
 * The clamp is not the limiting above, and it is not expected to fire: the plan
 * has already refused any gain that would take the clip's peak past the same
 * ceiling, so the loudest sample lands *on* it exactly. It is there because the
 * ceiling is measured per sample and inter-sample peaks are not, and a page that
 * goes to the reader a hundredth of a decibel over is worse than one that does
 * not.
 */
export async function renderLevelledAudio(
  source: AudioBufferLike,
  gainDb: number,
  ceilingDb = -1,
): Promise<AudioBuffer> {
  const gain = dbToGain(gainDb)
  const ceiling = dbToGain(ceilingDb)
  const channels = clamp(Math.round(source.numberOfChannels), 1, 2)
  const length = Math.max(1, source.length)
  const ctx = getAudioEngine().createOfflineContext(
    channels,
    length,
    source.sampleRate,
  )
  const out = ctx.createBuffer(channels, length, source.sampleRate)
  for (let c = 0; c < channels; c++) {
    const from = source.getChannelData(Math.min(c, source.numberOfChannels - 1))
    const data = out.getChannelData(c)
    for (let i = 0; i < length; i++) {
      data[i] = clamp((from[i] ?? 0) * gain, -ceiling, ceiling)
    }
  }
  return out
}

// ─── WAV encoding ─────────────────────────────────────────────────────────────

/**
 * 16-bit PCM WAV.
 *
 * WAV rather than a compressed format because the browser can *decode* every
 * audio codec we accept but can only *encode* PCM without a dependency — and a
 * preview that differs from what gets saved would be a lie. Speech at 44.1 kHz
 * is a few MB a minute, which is why the studio keeps the blob in memory rather
 * than inlining it.
 */
export function audioBufferToWav(buffer: AudioBufferLike): ArrayBuffer {
  const channels = clamp(Math.round(buffer.numberOfChannels), 1, 2)
  const frames = Math.max(0, buffer.length)
  const bytesPerSample = 2
  const blockAlign = channels * bytesPerSample
  const dataBytes = frames * blockAlign
  const out = new ArrayBuffer(44 + dataBytes)
  const view = new DataView(out)

  const writeAscii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++)
      view.setUint8(offset + i, text.charCodeAt(i))
  }

  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true)
  writeAscii(8, 'WAVE')
  writeAscii(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM chunk size
  view.setUint16(20, 1, true) // format: PCM
  view.setUint16(22, channels, true)
  view.setUint32(24, Math.round(buffer.sampleRate), true)
  view.setUint32(28, Math.round(buffer.sampleRate) * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, 8 * bytesPerSample, true)
  writeAscii(36, 'data')
  view.setUint32(40, dataBytes, true)

  const data: Float32Array[] = []
  for (let c = 0; c < channels; c++) {
    data.push(buffer.getChannelData(Math.min(c, buffer.numberOfChannels - 1)))
  }

  let offset = 44
  for (let frame = 0; frame < frames; frame++) {
    for (let c = 0; c < channels; c++) {
      const sample = clamp(data[c][frame], -1, 1)
      // Asymmetric scaling keeps positive and negative full scale exact.
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true,
      )
      offset += 2
    }
  }

  return out
}

// ─── Capability + decode ──────────────────────────────────────────────────────

/**
 * Whether this build can actually run the editor.
 *
 * Asked of the platform's engine rather than of two globals: the web has them,
 * and a phone has them through `react-native-audio-api`
 * (`#/lib/flashbookAudioEngineImpl.native`), which is what makes the desk — and
 * the Edit control that opens it — exist on a device instead of being hidden.
 */
export function canEditAudio(): boolean {
  return getAudioEngine().available()
}

/**
 * What the desk found in a clip's own bytes.
 *
 * Sniffed rather than trusted because the extension and the URL are both
 * unreliable — a recording is a blob with no name at all — and because the
 * container is what decides whether this browser can edit the take. "Opus in
 * WebM" is a perfectly good clip that Safari and every iPhone cannot open, and
 * saying so is the difference between a bug report and a fix.
 */
export type AudioContainer =
  | 'wav'
  | 'mp3'
  | 'mp4'
  | 'aac'
  | 'ogg'
  | 'flac'
  | 'webm'
  | 'aiff'
  | 'amr'
  | 'unknown'

/**
 * Why a clip could not be opened, as a *category* rather than a sentence.
 *
 * The distinction the old code did not make — and the reason a creator was told
 * "the browser could not decode it" when the truth was that the file never
 * arrived — is between the three ways this fails:
 *
 * - `unreadable`: the bytes could not be had (offline, a 404, a signed-out
 *   blob, a `file:` URI outside a browser). Nothing is wrong with the clip.
 * - `unsupported`: the bytes are here, and this browser has no decoder for
 *   them. Nothing can be done here, so the creator is told what to send instead.
 * - `too-large`: the bytes are here and would decode, but not inside the memory
 *   a phone has. Worth saying plainly rather than crashing the tab.
 */
export type AudioProblem =
  | 'no-engine'
  | 'unreadable'
  | 'unsupported'
  | 'too-large'

export class AudioOpenError extends Error {
  readonly problem: AudioProblem
  readonly container: AudioContainer
  /** A diagnostic detail, for the log and for tests — never shown raw. */
  readonly detail: string

  constructor(
    problem: AudioProblem,
    container: AudioContainer,
    detail: string,
  ) {
    super(`${problem}: ${detail}`)
    this.name = 'AudioOpenError'
    this.problem = problem
    this.container = container
    this.detail = detail
  }
}

/** Anything thrown while opening a clip, as an `AudioOpenError`. */
export function audioProblemOf(error: unknown): AudioOpenError {
  if (error instanceof AudioOpenError) return error
  const detail =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'unknown failure'
  return new AudioOpenError('unsupported', 'unknown', detail)
}

/** The container, named the way a creator would recognise it. */
export function audioContainerLabel(container: AudioContainer): string {
  switch (container) {
    case 'wav':
      return 'WAV'
    case 'mp3':
      return 'MP3'
    case 'mp4':
      return 'M4A'
    case 'aac':
      return 'AAC'
    case 'ogg':
      return 'OGG'
    case 'flac':
      return 'FLAC'
    case 'webm':
      return 'WebM'
    case 'aiff':
      return 'AIFF'
    case 'amr':
      return 'AMR'
    default:
      return ''
  }
}

/**
 * Containers WebKit will not open, whatever the clip sounds like.
 *
 * Opus inside WebM is the browser recorder's own default on Chrome, which is
 * exactly how a take ends up uneditable on an iPhone it was never recorded on.
 */
export function isWebKitHostileContainer(container: AudioContainer): boolean {
  return container === 'webm' || container === 'ogg'
}

const ascii = (bytes: Uint8Array, at: number, length: number) =>
  String.fromCharCode(...bytes.subarray(at, at + length))

/** The container of a clip, from its magic bytes. `unknown` when nothing matches. */
export function sniffAudioContainer(bytes: ArrayBuffer): AudioContainer {
  const view = new Uint8Array(bytes)
  if (view.length < 12) return 'unknown'
  const head = ascii(view, 0, 4)
  if (head === 'RIFF' && ascii(view, 8, 4) === 'WAVE') return 'wav'
  if (head === 'FORM' && ascii(view, 8, 4) === 'AIFF') return 'aiff'
  if (head === 'OggS') return 'ogg'
  if (head === 'fLaC') return 'flac'
  if (ascii(view, 0, 3) === 'ID3') return 'mp3'
  // ADTS (AAC) and MPEG audio share the frame-sync prefix — eleven set bits,
  // which is the top three of the second byte. The layer bits tell them apart:
  // Layer III (`0b01`) is MP3, and ADTS declares no layer at all.
  if (view[0] === 0xff && (view[1] & 0xe0) === 0xe0) {
    const layer = (view[1] >> 1) & 0x03
    return layer === 0 ? 'aac' : 'mp3'
  }
  if (
    view[0] === 0x1a &&
    view[1] === 0x45 &&
    view[2] === 0xdf &&
    view[3] === 0xa3
  )
    return 'webm'
  if (ascii(view, 4, 4) === 'ftyp') return 'mp4'
  if (ascii(view, 0, 5) === '#!AMR') return 'amr'
  return 'unknown'
}

/**
 * The bytes inside a `data:` URI.
 *
 * Read in place rather than fetched: `fetch` on a data URI is a round trip
 * through the network stack that some engines refuse for large payloads, and an
 * inline clip is precisely the case that must never fail.
 */
export function dataUrlBytes(url: string): ArrayBuffer {
  const comma = url.indexOf(',')
  if (comma < 0) throw new Error('a data URI with no payload')
  const meta = url.slice(5, comma)
  const payload = url.slice(comma + 1)
  if (!/;base64/i.test(meta)) {
    const text = decodeURIComponent(payload)
    const bytes = new Uint8Array(text.length)
    for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff
    return bytes.buffer
  }
  const binary = atob(payload)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

/**
 * The ceiling on a decode, in the float PCM it would hold.
 *
 * A decoded clip is `length × channels × 4` bytes of raw Float32, so an hour of
 * stereo is over half a gigabyte — a `decodeAudioData` that a desktop browser
 * shrugs at and a phone answers by killing the tab. Refused up front, as a
 * sentence the creator can act on, rather than as a crash they cannot.
 */
export const MAX_DECODE_BYTES = 256 * 1024 * 1024

/**
 * How much float PCM a decode would hold, from the compressed size.
 *
 * Compressed clips are estimated from a 128 kbps stream, which is what the
 * recorder writes; uncompressed ones are exact, since a WAV's 16-bit samples
 * simply double when widened to Float32.
 */
export function estimateDecodedBytes(
  bytes: number,
  container: AudioContainer,
): number {
  if (container === 'wav' || container === 'aiff') return bytes * 2
  const seconds = (bytes * 8) / 128_000
  return seconds * 44_100 * 2 * 4
}

/**
 * The bytes of a clip, whatever kind of URL is holding it.
 *
 * Callers that are *editing* pass the URL through `clipSourceFor`
 * (`#/lib/flashbookClips`) first, so a clip whose bytes are already on this device
 * is read from here rather than downloaded back: the desk must be able to open a
 * take the creator recorded ten seconds ago — on a train, with the network gone.
 */
export async function readAudioBytes(url: string): Promise<ArrayBuffer> {
  if (url.startsWith('data:')) {
    try {
      return dataUrlBytes(url)
    } catch (error) {
      throw new AudioOpenError(
        'unreadable',
        'unknown',
        `the inline clip is malformed: ${String(error)}`,
      )
    }
  }
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1]?.toLowerCase()
  const overTheWire = !scheme || scheme === 'http' || scheme === 'https'
  let bytes: ArrayBuffer

  if (overTheWire) {
    let response: Response
    try {
      response = await fetch(url)
    } catch (error) {
      throw new AudioOpenError(
        'unreadable',
        'unknown',
        `the clip could not be reached: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
    // A 403 or a 404 answers with a *body* — usually JSON. Handing that to the
    // decoder is what produced "the browser could not decode it" for a clip that
    // never arrived at all.
    if (!response.ok) {
      throw new AudioOpenError(
        'unreadable',
        'unknown',
        `the clip answered HTTP ${response.status}`,
      )
    }
    bytes = await response.arrayBuffer()
  } else {
    /*
     * Anything else is a clip only this device can reach: a `blob:` the browser is
     * holding, or a `file:` the phone wrote. Which of those is readable is a
     * platform fact — the browser cannot open a `file:` URL at all, while a phone
     * can open nothing else — so the engine answers it, and says so plainly when
     * the answer is no rather than letting the decoder take the blame.
     */
    try {
      bytes = await getAudioEngine().readLocalClip(url)
    } catch (error) {
      throw new AudioOpenError(
        'unreadable',
        'unknown',
        error instanceof Error ? error.message : String(error),
      )
    }
  }

  if (bytes.byteLength === 0) {
    throw new AudioOpenError('unreadable', 'unknown', 'the clip is empty')
  }
  const container = sniffAudioContainer(bytes)
  if (estimateDecodedBytes(bytes.byteLength, container) > MAX_DECODE_BYTES) {
    throw new AudioOpenError(
      'too-large',
      container,
      `${Math.round(bytes.byteLength / 1024 / 1024)} MB would not fit in memory`,
    )
  }
  return bytes
}

/**
 * Decodes a clip from bytes.
 *
 * Two attempts, each on a *fresh* context: a context that has just failed is a
 * context WebKit will keep failing, and on a phone the first attempt legitimately
 * loses a race with the audio device waking up. Browsers also cap how many
 * contexts a page may hold, so each one is closed as soon as the buffer is in
 * hand — the decoded `AudioBuffer` outlives it, being plain data.
 */
export async function decodeAudioBytes(
  bytes: ArrayBuffer,
): Promise<AudioBufferLike> {
  const engine = getAudioEngine()
  if (!engine.available()) {
    throw new AudioOpenError(
      'no-engine',
      'unknown',
      'Web Audio is not available',
    )
  }
  let lastError: unknown = null
  for (let attempt = 0; attempt < 2; attempt++) {
    const ctx = engine.createContext()
    try {
      // Safari hands out a suspended context until the tab has had a gesture;
      // decoding still works, but resuming first is what makes the *preview*
      // audible the instant the desk opens.
      try {
        await ctx.resume()
      } catch {}
      /*
       * A copy, always. `decodeAudioData` detaches the buffer it is given in
       * WebKit, so handing over the caller's array would leave the retry — and
       * the container sniffing after it — holding nothing at all.
       */
      return await ctx.decodeAudioData(bytes.slice(0))
    } catch (error) {
      lastError = error
    } finally {
      void ctx.close().catch(() => {})
    }
  }
  throw new AudioOpenError(
    'unsupported',
    sniffAudioContainer(bytes),
    lastError instanceof Error ? lastError.message : String(lastError),
  )
}

/**
 * Decoded clips, keyed by URL: reopening the editor must not re-decode.
 *
 * Bounded, because a decoded `AudioBuffer` is raw PCM — a handful of minutes of
 * speech is hundreds of megabytes — and a creator will replace a take many
 * times in one sitting. Re-decoding an evicted clip is cheap next to holding
 * every take they ever auditioned.
 */
const DECODE_CACHE_LIMIT = 4
const decodeCache = new Map<string, AudioBufferLike>()

/**
 * The decoded clip a URL holds, or the reason it could not be read.
 *
 * Keyed by the URL the *block* carries, never by whichever URL the bytes were
 * read from: an asset and its local mirror are the same clip, and decoding them
 * twice would give the desk two copies of one take.
 */
export async function decodeAudioForEditing(
  url: string,
): Promise<AudioBufferLike> {
  const cached = decodeCache.get(url)
  if (cached) return cached
  if (!canEditAudio()) {
    throw new AudioOpenError(
      'no-engine',
      'unknown',
      'this build has no Web Audio engine',
    )
  }

  const bytes = await readAudioBytes(clipSourceFor(url))
  let decoded: AudioBufferLike
  try {
    decoded = await decodeAudioBytes(bytes)
  } catch (error) {
    const failure = audioProblemOf(error)
    // The *container* is the actionable half of "cannot decode": it is what the
    // creator is told to replace.
    throw new AudioOpenError(
      failure.problem,
      sniffAudioContainer(bytes),
      failure.detail,
    )
  }

  decodeCache.set(url, decoded)
  while (decodeCache.size > DECODE_CACHE_LIMIT) {
    const oldest = decodeCache.keys().next().value
    if (oldest === undefined || oldest === url) break
    decodeCache.delete(oldest)
  }
  return decoded
}

/**
 * A reachable URL for a rendered clip.
 *
 * On the web that is a blob URL; on a phone it is a WAV written into the cache
 * directory, because React Native has no object URLs at all. Either way it is a
 * URL a player, a block's audio field and the upload pipeline can each reach,
 * which is the only property that matters here.
 *
 * `key` is the edit itself, so a platform that has to write a file reuses the
 * same one when the creator re-renders the same settings.
 */
export async function renderedClipUrl(
  buffer: AudioBufferLike,
  key: string,
): Promise<string> {
  return getAudioEngine().persistClip(audioBufferToWav(buffer), key)
}

// ─── Presentation helpers ─────────────────────────────────────────────────────

/** Seconds as `m:ss`, for the editor's clocks. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const total = Math.round(seconds)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** The one-line summary of an edit, for the collapsed field. */
export function describeSettings(settings: AudioEditSettings): string {
  const preset = matchAudioPreset(settings)
  if (preset) return getAudioPreset(preset).label
  const parts: string[] = []
  if (settings.room > 0.05) parts.push('room')
  if (settings.echo > 0.05) parts.push('echo')
  if (settings.clarity > 0.05) parts.push('clear')
  if (settings.warmth > 0.05) parts.push('warm')
  if (settings.depth > 0.05) parts.push('deep')
  if (settings.brightness > 0.05) parts.push('bright')
  if (settings.cleanup > 0.05) parts.push('clean')
  if (settings.evenness > 0.05) parts.push('even')
  if (Math.abs(settings.speed - 1) > 0.02)
    parts.push(`${settings.speed.toFixed(2)}×`)
  /*
   * The level is the one setting the desk offers no choice about — auto master
   * sets it from the clip's own loudness — so it has to be *said*, or a levelled
   * take would read as an untouched one.
   */
  if (Math.abs(settings.volume - 1) > 0.02)
    parts.push(`${Math.round(settings.volume * 100)}%`)
  return parts.length > 0 ? parts.join(' · ') : 'Custom'
}
